import {
  ButtonList,
  DropdownButton,
  DropdownButtonContext,
} from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';

import { UserProfile } from 'components/userProfile/userProfile';
import { useCurrentUser } from 'utils/user';

import { useConversationRow } from '../../../../../utils/useConversationRow';

import { ConversationsTableRowMenuActivityLog } from './components/items/activityLog/conversationsTableRowMenuActivityLog';
import { ConversationsTableRowMenuClose } from './components/items/close/conversationsTableRowMenuClose';
import { ConversationsTableRowMenuFilter } from './components/items/filter/conversationsTableRowMenuFilter';
import { ConversationsTableRowMenuTransferToQueue } from './components/items/transferToQueue/conversationsTableRowMenuTransferToQueue';
import { ConversationsTableRowMenuUser } from './components/items/user/conversationsTableRowMenuUser';
import styles from './conversationsTableRowMenu.module.scss';

/**
 * Represents the component for displaying various actions that can be performed
 * on each conversation in the table.
 */
export const ConversationsTableRowMenu = (): JSX.Element => {
  const {
    conversationAndRequester: { conversation, requester },
  } = useConversationRow();
  const { user } = useCurrentUser();
  const canTransferConversation =
    conversation.initial_channel !== 'callback' &&
    (!conversation.assignment || user.id === conversation.assignment.agent_id);
  const canCloseOrEndConversation =
    !conversation.is_closed && !conversation.assignment;
  const hasLogAccess = useHasAccess('conversation.log');

  return (
    <DropdownButton
      noArrow
      fixed
      icon="dots-v"
      buttonProps={{ variant: 'tertiary' }}
      dropdownProps={{ className: styles.dropdown, fixed: true }}
      dropdown={
        <DropdownButtonContext.Consumer>
          {({ toggleDropdown }) => (
            <ButtonList>
              {canTransferConversation && (
                <ConversationsTableRowMenuTransferToQueue
                  conversation={conversation}
                  toggleDropdown={toggleDropdown}
                />
              )}

              {hasLogAccess && (
                <ConversationsTableRowMenuActivityLog
                  conversation={conversation}
                />
              )}

              {canCloseOrEndConversation && (
                <ConversationsTableRowMenuClose
                  conversation={conversation}
                  toggleDropdown={toggleDropdown}
                />
              )}

              <div className={styles.header}>
                <UserProfile
                  user={requester}
                  truncateName
                  nameClassName={styles.userName}
                />
              </div>
              <ConversationsTableRowMenuUser user={requester} />
              <ConversationsTableRowMenuFilter
                user={requester}
                toggleDropdown={toggleDropdown}
              />

              {conversation.assignment?.agent && (
                <>
                  <div className={styles.header}>
                    <UserProfile
                      user={conversation.assignment?.agent ?? undefined}
                      truncateName
                      nameClassName={styles.userName}
                    />
                  </div>

                  <ConversationsTableRowMenuUser
                    user={conversation.assignment.agent}
                    isAssignee
                  />

                  <ConversationsTableRowMenuFilter
                    user={conversation.assignment.agent}
                    isAssignee
                    toggleDropdown={toggleDropdown}
                  />
                </>
              )}
            </ButtonList>
          )}
        </DropdownButtonContext.Consumer>
      }
    />
  );
};
